import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import HtmlTooltip from "./HtmlTooltip";
import ErrorMessageWithErrorIcon from "components/Elements/ErrorMessageWithErrorIcon";
import HasImages from "assets/icons/images.svg";
import HasDocuments from "assets/icons/documents.svg";
import HasQuestion from "assets/icons/question_dark.svg";
import HasTools from "assets/icons/tools.svg";
import HasParts from "assets/icons/parts.svg";
import HasPermits from "assets/icons/permit.svg";
import SafteryCritical from "assets/icons/safety-critical.svg";

const useStyles = makeStyles()(() => ({
	logoContainer: {
		display: "flex",
		// A flex container only wraps its children against its OWN box - with
		// no width set here, table cells don't clip by default, so this grew
		// to fit every icon on one line and spilled past the column into
		// neighbouring cells instead of ever wrapping. width: 100% ties it to
		// the (fixed, 170px) column so a full house of icons wraps within the
		// cell instead.
		width: "100%",
		flexWrap: "wrap",
		gap: "5px",
		alignItems: "center",
		alignContent: "center",
		minWidth: "100px",
		overflow: "hidden",
	},
	iconSize: { width: "20px" },
}));

// The "logo" pseudo-column: a tray of status icons (errors, safety-critical,
// and what the task has attachments for) shown in place of a plain value cell.
const TaskRowStatusIcons = ({
	taskError,
	toggle,
	hasSafetyCritical,
	hasParts,
	hasTools,
	hasPermits,
	hasImages,
	hasQuestions,
	hasDocuments,
	toolPlural,
	partPlural,
	permitPlural,
	questionPlural,
	safetyCritical,
}) => {
	const { classes, cx } = useStyles();
	const { t } = useTranslation();
	const whiteFilter = cx({ "white-filter": toggle });

	return (
		<div className={classes.logoContainer}>
			{Object.values(taskError)?.filter(Boolean).length > 0 && (
				<span>
					<ErrorMessageWithErrorIcon message={Object.values(taskError)?.filter(Boolean)} />
				</span>
			)}
			{hasSafetyCritical && (
				<HtmlTooltip title={t("label.isEntity", { entity: safetyCritical })}>
					<span>
						<img className={whiteFilter} src={SafteryCritical} alt="" />
					</span>
				</HtmlTooltip>
			)}
			{hasParts && (
				<HtmlTooltip title={t("label.hasEntity", { entity: partPlural })}>
					<span>
						<img className={whiteFilter} src={HasParts} alt="" />
					</span>
				</HtmlTooltip>
			)}
			{hasTools && (
				<span>
					<HtmlTooltip title={t("label.hasEntity", { entity: toolPlural })}>
						<img className={whiteFilter} src={HasTools} alt="" />
					</HtmlTooltip>
				</span>
			)}
			{hasPermits && (
				<HtmlTooltip title={t("label.hasEntity", { entity: permitPlural })}>
					<span>
						<img className={whiteFilter} src={HasPermits} alt="" />
					</span>
				</HtmlTooltip>
			)}
			{hasImages && (
				<span>
					<HtmlTooltip title={t("title.hasImages")}>
						<img className={whiteFilter} src={HasImages} alt="" />
					</HtmlTooltip>
				</span>
			)}
			{hasQuestions && (
				<HtmlTooltip title={t("label.hasEntity", { entity: questionPlural })}>
					<span>
						<img className={cx(classes.iconSize, whiteFilter)} src={HasQuestion} alt="" />
					</span>
				</HtmlTooltip>
			)}
			{hasDocuments && (
				<HtmlTooltip title={t("label.hasAttachments")}>
					<span>
						<img className={whiteFilter} src={HasDocuments} alt="" />
					</span>
				</HtmlTooltip>
			)}
		</div>
	);
};

TaskRowStatusIcons.propTypes = {
	taskError: PropTypes.object.isRequired,
	toggle: PropTypes.bool.isRequired,
	hasSafetyCritical: PropTypes.bool,
	hasParts: PropTypes.bool,
	hasTools: PropTypes.bool,
	hasPermits: PropTypes.bool,
	hasImages: PropTypes.bool,
	hasQuestions: PropTypes.bool,
	hasDocuments: PropTypes.bool,
	toolPlural: PropTypes.string,
	partPlural: PropTypes.string,
	permitPlural: PropTypes.string,
	questionPlural: PropTypes.string,
	safetyCritical: PropTypes.string,
};

export default TaskRowStatusIcons;
